<template>
  <div class="flex w-full justify-between space-x-4 border-b pb-4 pt-2">
    <div
      class="group relative flex h-60 w-2/5 items-center justify-center rounded border p-2 overflow-hidden"
    >
      <div
        class="absolute inset-0 z-10 flex cursor-pointer items-center justify-center"
      >
        <UIButton
          id="edit-sms-template"
          size="small"
          class="z-20 opacity-0 group-hover:opacity-100"
          @click="openSmsTemplateEditor"
        >
          <Pencil01Icon class="h-6 w-6 text-black" />
          <span class="ml-2 text-black">
            {{ t('calendar_advanced.notifications.edit') }}
          </span>
        </UIButton>
      </div>
      <div :class="'sms-preview-container ' + (activePreview ? 'blur-sm' : '')">
        <div class="sms-preview">
          <!-- SMS conversation screen -->
          <div class="message-conversation">
            <!-- Sent message bubble (right-aligned, blue) -->
            <div class="message-bubble sent-message">
              <div class="sms-text" :class="getTextSizeClass(smsBody)">
                {{ smsBody }}
              </div>
              <div class="message-status">
                <span class="delivered-text">
                  {{ t('calendar_advanced.notifications.delivered') }}
                </span>
              </div>
            </div>
            <div class="message-timestamp">
              {{ getCurrentTime() }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="align-start flex w-3/5 flex-col justify-start space-y-2 text-left text-xs text-gray-500"
    >
      <div class="py-1">
        <div class="pb-1">
          {{ t('calendar_advanced.notifications.created_on') }}
        </div>
        <div class="text-xs text-gray-900">
          {{ formatDate(createdAt, 'MMMM DD,YYYY hh:mm A') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Pencil01Icon } from '@gohighlevel/ghl-icons/24/outline'
import { UIButton } from '@gohighlevel/ghl-ui'
import moment from 'moment'
import { defineComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'EditSmsTemplate',
  components: {
    Pencil01Icon,
    UIButton,
  },
  props: {
    templateId: {
      type: String,
      required: true,
    },
    smsBody: {
      type: String,
      default: '',
    },
    createdAt: {
      type: String,
      default: '',
    },
  },
  emits: ['edit'],
  setup(props, { emit }) {
    const activePreview = ref(false)
    
    const { t } = useI18n()

    function formatDate(date: any, format = 'YYYY-MM-DD') {
      if (!date) return t('calendar_advanced.notifications.not_available')
      return moment(date).format(format)
    }
    
    function openSmsTemplateEditor() {
      emit('edit', props.templateId)
    }
    
    function getTextSizeClass(text: string) {
      if (!text) return 'text-normal'
      
      const length = text.length
      if (length > 300) return 'text-xs'      // Very long messages
      if (length > 200) return 'text-sm'      // Long messages  
      if (length > 100) return 'text-normal'  // Medium messages
      return 'text-normal'                    // Short messages
    }
    
    function getCurrentTime() {
      return moment().format('h:mm A')
    }
    
    return {
      activePreview,
      formatDate,
      openSmsTemplateEditor,
      getTextSizeClass,
      getCurrentTime,
      t,
    }
  },
})
</script>

<style lang="scss" scoped>
.sms-preview-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow: hidden;
  
  .sms-preview {
    max-width: 100%;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    
    .message-conversation {
      width: 100%;
      height: 100%;
      padding: 12px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      background: #f8f9fa;
      border-radius: 8px;
      overflow-y: auto;
      
      .message-bubble {
        margin-bottom: 4px;
        max-width: 85%;
        
        &.sent-message {
          align-self: flex-end;
          background: #007aff;
          color: white;
          border-radius: 18px 18px 4px 18px;
          padding: 8px 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          max-height: 180px;
          overflow-y: auto;
          
          .sms-text {
            line-height: 1.4;
            white-space: pre-wrap;
            word-break: break-word;
            margin: 0;
            
            &.text-normal {
              font-size: 0.625rem; /* 10px */
            }
            
            &.text-sm {
              font-size: 0.5625rem; /* 9px */
              line-height: 1.3;
            }
            
            &.text-xs {
              font-size: 0.5rem; /* 8px */
              line-height: 1.25;
            }
          }
          
          .message-status {
            margin-top: 3px;
            text-align: right;
            
            .delivered-text {
              font-size: 0.625rem; /* 10px */
              color: rgba(255, 255, 255, 0.7);
              font-weight: 400;
            }
          }
        }
      }
      
      .message-timestamp {
        align-self: flex-end;
        font-size: 0.6875rem; /* 11px */
        color: #8e8e93;
        margin-top: 2px;
        margin-right: 4px;
        font-weight: 400;
      }
    }
  }
}
</style>
